import React from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiGlobe, FiCheck } from 'react-icons/fi';
import { LocationSearchInput } from './LocationSearchInput';
import type { LocationBody } from '../../services/locationService';
import { useDiscoveryStore } from '../../store/useDiscoveryStore';

interface LocationModalProps {
  onClose: () => void;
}

export const LocationModal: React.FC<LocationModalProps> = ({ onClose }) => {
  const { selectedCity, setSelectedCity } = useDiscoveryStore();

  const handleLocationSelected = (location: LocationBody | null) => {
    if (location) {
      setSelectedCity(location.city);
      onClose();
    }
  };

  const selectAllLocations = () => {
    setSelectedCity(null);
    onClose();
  };

  const handleBackdropClick = (e: React.PointerEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-110 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onPointerDown={handleBackdropClick}
    >
      <div
        className="w-full max-w-lg bg-[#0c0c0c] border border-white/10 rounded-[32px] overflow-hidden shadow-2xl flex flex-col"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-8 py-6 border-b border-white/5">
          <div>
            <h3 className="text-white text-[20px] font-semibold">Discovery Location</h3>
            <p className="text-zinc-500 text-[13px] mt-0.5">Filter content based on your city</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 flex-1 overflow-y-auto max-h-[70vh] no-scrollbar flex flex-col gap-6">
          {/* All Locations Option */}
          <button
            onClick={selectAllLocations}
            className={`flex items-center justify-between w-full p-5 rounded-2xl border transition-all text-left group ${
              selectedCity === null
                ? 'bg-blue-600/10 border-blue-500/50'
                : 'bg-zinc-900/40 border-white/5 hover:border-white/20 hover:bg-white/5'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                  selectedCity === null
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-zinc-800 text-zinc-500 group-hover:text-zinc-300'
                }`}
              >
                <FiGlobe size={24} />
              </div>
              <div>
                <p
                  className={`font-semibold text-[16px] ${selectedCity === null ? 'text-white' : 'text-zinc-300 group-hover:text-white'}`}
                >
                  All Locations
                </p>
                <p className="text-zinc-500 text-[13px]">Show content from everywhere</p>
              </div>
            </div>
            {selectedCity === null && (
              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white">
                <FiCheck size={14} strokeWidth={3} />
              </div>
            )}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-white/5"></div>
            </div>
            <div className="relative flex justify-center text-[12px] uppercase tracking-widest font-bold">
              <span className="bg-[#0c0c0c] px-4 text-zinc-600">Or search specific city</span>
            </div>
          </div>

          {/* Search Input */}
          <div className="flex flex-col gap-4">
            <LocationSearchInput
              onLocationSelected={handleLocationSelected}
              placeholder="Type to search city..."
              lax={true} // Allow picking cities without full address
            />
          </div>
        </div>

        {/* Footer info */}
        <div className="px-8 py-5 bg-white/2[0.02] border-t border-white/5 text-center">
          <p className="text-zinc-600 text-[11px] font-medium uppercase tracking-wider">
            Powered by Google Maps
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>,
    document.body,
  );
};
